import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';

const ThemeContext = createContext(null);

// ThemeConfig의 defaultConfig 값을 그대로 가져옴
const defaultConfig = {
  // 메타데이터
  workspaceId: 'default',
  createdAt: null,
  updatedAt: null,

  // 테마 설정
  navLayout: 'top',
  submenuType: 'mega', // 상단 배치일 때: 'mega' | 'dropdown'
  menuScrollType: 'wrap',
  fixedMenuCount: 6,
  menuAlign: 'left',
  mobileMenuStyle: 'modern',
  themeColor: '#e97730',
  themeColorSecondary: '#146e7a',
  headerTopBackgroundColor: '#f8f8f8',
  headerTopTextColor: '#000000',
  sidebarColumns: 3,
  gridGap: 1.25,
  containerWidths: {
    xl: 1400,
    lg: 992,
    md: 768,
    sm: 576
  },
  areaContainerWidths: {
    'header-top': { xl: null, lg: null, md: null, sm: null },
    'header-bottom': { xl: null, lg: null, md: null, sm: null },
    'banner': { xl: null, lg: null, md: null, sm: null },
    'content': { xl: null, lg: null, md: null, sm: null },
    'footer': { xl: null, lg: null, md: null, sm: null },
    'related-sites': { xl: null, lg: null, md: null, sm: null }
  },
  fontStack: '"Pretendard", "Noto Sans KR", Helvetica, Arial, serif',
  primaryFont: 'Pretendard',
  fallbackFonts: '"Noto Sans KR", Helvetica, Arial, serif',
  rightSidebarType: 'simple',
  rightSidebarWidth: 420,
  footerTheme: 'simple',
  copyrightAlignment: 'center',
  copyrightBackgroundColor: '#343a40',
  copyrightTextColor: '#ffffff',
  copyrightHeight: 10,
  bannerType: 'center-image-dynamic',
  sidebarMenuType: 'accordion',
  headerTopHeight: 10,
  headerBottomHeight: 15,
  headerTopVisible: true,
  megaMenuColumns: 1,
  megaMenuSectionMaxColumns: 4,
  megaMenuStyle: 'default', // 메가메뉴 스타일 유형: 'default' | 'glow'
  headerBottomBackgroundColor: '#ffffff',
  headerBottomTextColor: '#000000',
  buttonTheme: 'btn-theme01',
  aosEnabled: true, // AOS 공통 기능 활성화 여부
  relatedSitesEnabled: true, // 연관사이트 영역 표시 여부
  relatedSitesType: 'image-thumbnail', // 연관사이트 유형: 'image-thumbnail' | 'combo' | 'list'
  relatedSitesShowTitle: true, // 이미지썸네일 유형: 하단 사이트 제목 표시 여부
  relatedSitesShowImage: true, // 리스트형: 이미지 표시 여부
  relatedSitesShowUrl: false, // 리스트형: URL 주소 표시 여부
  relatedSitesListExpanded: false, // 리스트형: 리스트 펼침 여부 (기본값: 접힘)
  // 헤더 로고 설정 (HeaderAreaManager.jsx getDefaultHeaderLogo() 구조와 동일, 상단 로고 관리 페이지에서 편집)
  headerLogo: {
    type: 'image', // 'image' | 'tag'
    image: '/assets/images/logo/logo.png', // 이미지 타입일 때 사용
    // 태그 타입일 때 사용 (다국어: title/subtitle은 { kor, eng })
    tag: {
      logo: {
        size: 49,
        backgroundColor: 'rgb(147, 51, 234)',
        color: '#ffffff',
        fontSize: '1.5rem',
        letter: 'G'
      },
      title: { kor: 'GLOW Platform', eng: 'GLOW Platform' },
      titleColor: '#6b21a8',
      titleStyle: { fontSize: '20px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '0', textTransform: 'none' },
      subtitle: { kor: '지산학 R&D 플랫폼', eng: 'Industry-Academia R&D Platform' },
      subtitleColor: '#6b7280',
      subtitleStyle: { fontSize: '14px', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0', textTransform: 'none' }
    }
  }
};

const STORAGE_KEY = 'themeConfig_local';
const STORAGE_MODE_KEY = 'themeConfigStorageMode';

// 유틸리티 함수들
const getCurrentISODate = () => new Date().toISOString();

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const deepMerge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
};

const isObject = (item) => item && typeof item === 'object' && !Array.isArray(item);

const getInitializedDefaultConfig = () => {
  // window.__themeConfig가 이미 설정되어 있으면 사용 (DB에서 로드한 값)
  if (typeof window !== 'undefined' && window.__themeConfig) {
    return deepClone(window.__themeConfig);
  }

  // 기본값 사용
  const config = deepClone(defaultConfig);
  const now = getCurrentISODate();
  config.createdAt = now;
  config.updatedAt = now;
  return config;
};

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(getInitializedDefaultConfig);

  const [showThemePanel, setShowThemePanel] = useState(false);
  const configRef = useRef(themeConfig);

  // configRef 업데이트
  useEffect(() => {
    configRef.current = themeConfig;
  }, [themeConfig]);

  // 마운트 시 window.__themeConfig가 이미 설정되어 있으면 즉시 반영
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__themeConfig) {
      // window.__themeConfig가 현재 themeConfig와 다르면 업데이트
      const currentConfigStr = JSON.stringify(configRef.current);
      const windowConfigStr = JSON.stringify(window.__themeConfig);
      if (currentConfigStr !== windowConfigStr) {
        setThemeConfig(deepClone(window.__themeConfig));
      }
    }
  }, []); // 마운트 시 한 번만 실행

  // ThemeConfig.get() 대응
  const get = useCallback((key) => {
    if (!key) return configRef.current;
    const keys = key.split('.');
    let value = configRef.current;
    for (let i = 0; i < keys.length; i++) {
      if (value && typeof value === 'object' && keys[i] in value) {
        value = value[keys[i]];
      } else {
        return undefined;
      }
    }
    return value;
  }, []);

  // ThemeConfig.set() 대응
  const set = useCallback((key, value, autoSave = true, updateTimestamp = true) => {
    setThemeConfig(prev => {
      const newConfig = deepClone(prev);
      const keys = key.split('.');
      let target = newConfig;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!target[keys[i]] || typeof target[keys[i]] !== 'object') {
          target[keys[i]] = {};
        }
        target = target[keys[i]];
      }

      target[keys[keys.length - 1]] = value;

      if (updateTimestamp) {
        newConfig.updatedAt = getCurrentISODate();
      }

      if (autoSave) {
        try {
          const storageMode = localStorage.getItem(STORAGE_MODE_KEY) || 'local';
          if (storageMode === 'local') {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
          }
        } catch (e) {
          console.warn('테마 설정 저장 실패:', e);
        }
      }

      // window.__themeConfig도 동기화
      if (typeof window !== 'undefined') {
        window.__themeConfig = deepClone(newConfig);
      }

      return newConfig;
    });
  }, []);

  // ThemeConfig.reset() 대응
  const reset = useCallback((preserveMetadata = false) => {
    const currentWorkspaceId = preserveMetadata ? configRef.current.workspaceId : null;
    const currentCreatedAt = preserveMetadata ? configRef.current.createdAt : null;

    const newConfig = getInitializedDefaultConfig();
    if (preserveMetadata && currentWorkspaceId) {
      newConfig.workspaceId = currentWorkspaceId;
    }
    if (preserveMetadata && currentCreatedAt) {
      newConfig.createdAt = currentCreatedAt;
    }

    setThemeConfig(newConfig);
    try {
      const storageMode = localStorage.getItem(STORAGE_MODE_KEY) || 'local';
      if (storageMode === 'local') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
      }
    } catch (e) {
      console.warn('테마 설정 저장 실패:', e);
    }
    return newConfig;
  }, []);

  // ThemeConfig.toJSON() 대응
  const toJSON = useCallback(() => {
    return JSON.stringify(configRef.current, null, 2);
  }, []);

  // ThemeConfig.fromJSON() 대응
  const fromJSON = useCallback((jsonString, preserveMetadata = false, autoSave = true) => {
    try {
      const parsed = JSON.parse(jsonString);
      const currentWorkspaceId = preserveMetadata ? configRef.current.workspaceId : null;
      const currentCreatedAt = preserveMetadata ? configRef.current.createdAt : null;
      const initialized = getInitializedDefaultConfig();

      const merged = deepMerge({}, initialized, parsed);

      if (preserveMetadata && currentWorkspaceId) {
        merged.workspaceId = currentWorkspaceId;
      }
      if (preserveMetadata && currentCreatedAt) {
        merged.createdAt = currentCreatedAt;
      }

      if (!merged.createdAt) {
        merged.createdAt = getCurrentISODate();
      }

      setThemeConfig(merged);

      if (autoSave) {
        try {
          const storageMode = localStorage.getItem(STORAGE_MODE_KEY) || 'local';
          if (storageMode === 'local') {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          }
        } catch (e) {
          console.warn('테마 설정 저장 실패:', e);
        }
      }

      return merged;
    } catch (e) {
      console.error('JSON 파싱 오류:', e);
      return null;
    }
  }, []);

  // 저장 모드 관리
  const getStorageMode = useCallback(() => {
    try {
      const mode = localStorage.getItem(STORAGE_MODE_KEY);
      return mode === 'local' || mode === 'DB' || mode === 'none' ? mode : 'local';
    } catch (e) {
      return 'local';
    }
  }, []);

  const setStorageMode = useCallback((mode) => {
    try {
      if (mode === 'local' || mode === 'DB' || mode === 'none') {
        localStorage.setItem(STORAGE_MODE_KEY, mode);
        return true;
      }
      return false;
    } catch (e) {
      console.warn('저장 모드 설정 중 오류:', e);
      return false;
    }
  }, []);

  const toggleThemePanel = useCallback(() => {
    setShowThemePanel(prev => !prev);
  }, []);

  // ThemeApply.js 로직: CSS 변수 및 클래스 적용
  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    // window.__themeConfig를 우선 사용 (DB에서 로드한 최신 값)
    const config = (typeof window !== 'undefined' && window.__themeConfig) || themeConfig;

    // CSS 변수 업데이트를 배치 처리하여 DOM 변경 최소화
    const updates = [];

    // 1. 테마 색상 (주/보조 분리)
    const themeSecondaryDefault = '#146e7a';
    if (config.themeColor) {
      const newValue = config.themeColor;
      if (computedStyle.getPropertyValue('--theme-color-primary').trim() !== newValue) {
        updates.push(['--theme-color-primary', newValue]);
      }
    }
    const secondaryResolved =
      config.themeColorSecondary != null && String(config.themeColorSecondary).trim() !== ''
        ? String(config.themeColorSecondary).trim()
        : themeSecondaryDefault;
    if (computedStyle.getPropertyValue('--theme-color-secondary').trim() !== secondaryResolved) {
      updates.push(['--theme-color-secondary', secondaryResolved]);
    }

    // 2. 최상단 영역
    if (config.headerTopBackgroundColor) {
      const newValue = config.headerTopBackgroundColor;
      if (computedStyle.getPropertyValue('--header-top-bg-color').trim() !== newValue) {
        updates.push(['--header-top-bg-color', newValue]);
      }
    }
    if (config.headerTopTextColor) {
      const newValue = config.headerTopTextColor;
      if (computedStyle.getPropertyValue('--header-top-text-color').trim() !== newValue) {
        updates.push(['--header-top-text-color', newValue]);
      }
    }
    if (config.headerTopHeight !== undefined && config.headerTopHeight !== null) {
      const heightPx = `${config.headerTopHeight}px`;
      if (computedStyle.getPropertyValue('--header-top-padding').trim() !== heightPx) {
        updates.push(['--header-top-padding', heightPx]);
      }
      const headerTopEl = document.querySelector('.header-top');
      if (headerTopEl) {
        headerTopEl.style.paddingTop = heightPx;
        headerTopEl.style.paddingBottom = heightPx;
      }
    }
    if (config.headerTopVisible !== undefined && config.headerTopVisible !== null) {
      const headerTopEl = document.querySelector('.header-top');
      if (headerTopEl) {
        if (config.headerTopVisible === true || config.headerTopVisible === 'true') {
          headerTopEl.classList.remove('hidden');
          headerTopEl.style.removeProperty('display');
        } else {
          headerTopEl.classList.add('hidden');
          headerTopEl.style.removeProperty('display');
        }
      }
    }

    // 3. 메뉴 영역
    if (config.headerBottomBackgroundColor) {
      const newValue = config.headerBottomBackgroundColor;
      if (computedStyle.getPropertyValue('--header-bottom-bg-color').trim() !== newValue) {
        updates.push(['--header-bottom-bg-color', newValue]);
      }
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.backgroundColor = config.headerBottomBackgroundColor;
      }
    }
    if (config.headerBottomTextColor) {
      const newValue = config.headerBottomTextColor;
      if (computedStyle.getPropertyValue('--header-bottom-text-color').trim() !== newValue) {
        updates.push(['--header-bottom-text-color', newValue]);
      }
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.color = config.headerBottomTextColor;
      }
    }
    if (config.headerBottomHeight !== undefined && config.headerBottomHeight !== null) {
      const heightPx = `${config.headerBottomHeight}px`;
      if (computedStyle.getPropertyValue('--header-bottom-padding').trim() !== heightPx) {
        updates.push(['--header-bottom-padding', heightPx]);
      }
      if (computedStyle.getPropertyValue('--header-bottom-padding-bottom').trim() !== heightPx) {
        updates.push(['--header-bottom-padding-bottom', heightPx]);
      }
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.paddingTop = heightPx;
        headerBottomEl.style.paddingBottom = heightPx;
      }
    }

    // 3-1. 상단 메뉴 정렬 및 초기 스크롤 위치
    if (config.menuAlign) {
      const align = config.menuAlign === 'right' ? 'right' : 'left';
      const menuLists = document.querySelectorAll('.header-area .main-menu > nav > ul.main-menu-list');
      menuLists.forEach((list) => {
        list.style.justifyContent = 'flex-start';
      });
      const navs = document.querySelectorAll('.header-area .main-menu > nav');
      navs.forEach((nav) => {
        const maxScrollLeft = Math.max(0, nav.scrollWidth - nav.clientWidth);
        nav.scrollLeft = align === 'right' ? maxScrollLeft : 0;
      });
    }

    // 4. 그리드 간격
    if (config.gridGap !== undefined && config.gridGap !== null) {
      const newValue = `${config.gridGap.toFixed(2)}rem`;
      if (computedStyle.getPropertyValue('--grid-gap').trim() !== newValue) {
        updates.push(['--grid-gap', newValue]);
      }
    }

    // 5. 컨테이너 너비
    if (config.containerWidths) {
      Object.keys(config.containerWidths).forEach(size => {
        if (config.containerWidths[size] !== undefined && config.containerWidths[size] !== null) {
          const varName = `--container-max-width-${size}`;
          const newValue = `${config.containerWidths[size]}px`;
          if (computedStyle.getPropertyValue(varName).trim() !== newValue) {
            updates.push([varName, newValue]);
          }
        }
      });
    }

    // 6. 영역별 컨테이너 너비
    if (config.areaContainerWidths) {
      Object.keys(config.areaContainerWidths).forEach(area => {
        const areaWidths = config.areaContainerWidths[area];
        if (areaWidths) {
          Object.keys(areaWidths).forEach(size => {
            if (areaWidths[size] !== undefined && areaWidths[size] !== null) {
              const varName = `--container-max-width-${area}-${size}`;
              const newValue = `${areaWidths[size]}px`;
              if (computedStyle.getPropertyValue(varName).trim() !== newValue) {
                updates.push([varName, newValue]);
              }
            }
          });
        }
      });
    }

    // 7. 폰트 스택
    if (config.fontStack) {
      const newValue = config.fontStack;
      if (computedStyle.getPropertyValue('--bs-font-sans-serif').trim() !== newValue) {
        updates.push(['--bs-font-sans-serif', newValue]);
      }
      document.body.style.fontFamily = config.fontStack;
    }

    // 8. 메가메뉴 컬럼 수
    if (config.megaMenuColumns !== undefined && config.megaMenuColumns !== null) {
      const columns = parseInt(config.megaMenuColumns, 10);
      if (!isNaN(columns) && columns >= 1 && columns <= 3) {
        const newValue = columns.toString();
        if (computedStyle.getPropertyValue('--mega-menu-columns').trim() !== newValue) {
          updates.push(['--mega-menu-columns', newValue]);
        }
      }
    }

    // 8-1. 메가메뉴 섹션 최대 컬럼 수
    if (config.megaMenuSectionMaxColumns !== undefined && config.megaMenuSectionMaxColumns !== null) {
      const maxColumns = parseInt(config.megaMenuSectionMaxColumns, 10);
      if (!isNaN(maxColumns) && maxColumns >= 1 && maxColumns <= 12) {
        const newValue = maxColumns.toString();
        if (computedStyle.getPropertyValue('--mega-menu-section-max-columns').trim() !== newValue) {
          updates.push(['--mega-menu-section-max-columns', newValue]);
        }
      }
    }

    // 8-2. 사이드바 컬럼 수
    if (config.sidebarColumns !== undefined && config.sidebarColumns !== null) {
      const sidebarCols = parseInt(config.sidebarColumns, 10);
      if (!isNaN(sidebarCols) && sidebarCols >= 1 && sidebarCols <= 11) {
        const newValue = sidebarCols.toString();
        if (computedStyle.getPropertyValue('--sidebar-columns').trim() !== newValue) {
          updates.push(['--sidebar-columns', newValue]);
        }
      }
    }

    // 9. 우측 사이드바 너비
    if (config.rightSidebarWidth !== undefined && config.rightSidebarWidth !== null) {
      const widthPx = `${config.rightSidebarWidth}px`;
      if (computedStyle.getPropertyValue('--right-sidebar-width').trim() !== widthPx) {
        updates.push(['--right-sidebar-width', widthPx]);
      }
      const sidebarElements = document.querySelectorAll('.sidebar-common-active');
      sidebarElements.forEach(el => {
        el.style.width = widthPx;
      });
    }

    // 10. Footer 설정
    if (config.copyrightBackgroundColor) {
      const newValue = config.copyrightBackgroundColor;
      if (computedStyle.getPropertyValue('--copyright-background-color').trim() !== newValue) {
        updates.push(['--copyright-background-color', newValue]);
      }
    }

    // 변경된 CSS 변수들을 한 번에 배치 처리 (DOM 변경 최소화)
    if (updates.length > 0) {
      requestAnimationFrame(() => {
        updates.forEach(([name, value]) => {
          root.style.setProperty(name, value);
        });
      });
    }
    if (config.copyrightTextColor) {
      const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
      const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;
      if (copyrightEl) copyrightEl.style.color = config.copyrightTextColor;
      if (copyrightP) copyrightP.style.color = config.copyrightTextColor;
    }
    if (config.copyrightHeight !== undefined && config.copyrightHeight !== null) {
      const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
      if (copyrightEl) {
        const heightPx = `${config.copyrightHeight}px`;
        copyrightEl.style.paddingTop = heightPx;
        copyrightEl.style.paddingBottom = heightPx;
      }
    }

    // 11. 네비게이션 레이아웃 (Body Class)
    document.body.classList.remove('nav-layout-top', 'nav-layout-left', 'nav-layout-right');
    if (config.navLayout) {
      document.body.classList.add(`nav-layout-${config.navLayout}`);
    }

    // 12. Footer 테마 (window.__themeConfig 우선 사용)
    const footerTheme = (typeof window !== 'undefined' && window.__themeConfig && window.__themeConfig.footerTheme)
      || config.footerTheme;
    if (footerTheme) {
      const footerArea = document.querySelector('.footer-area');
      if (footerArea) {
        footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant');
        footerArea.classList.add(`footer-theme-${footerTheme}`);
      }
    }

    // 13. 메가메뉴 스타일 유형 (CSS 동적 로드)
    const megaMenuStyle = (typeof window !== 'undefined' && window.__themeConfig && window.__themeConfig.megaMenuStyle)
      || config.megaMenuStyle || 'default';

    // 기존 메가메뉴 스타일 CSS 링크 확인
    const existingMegaMenuStyleLink = document.querySelector('link[data-mega-menu-style]');

    if (megaMenuStyle === 'glow') {
      // Glow 스타일: 링크가 없거나 다른 스타일이면 추가
      if (!existingMegaMenuStyleLink || existingMegaMenuStyleLink.getAttribute('data-mega-menu-style') !== 'glow') {
        // 기존 링크가 있으면 제거
        if (existingMegaMenuStyleLink) {
          existingMegaMenuStyleLink.remove();
        }
        // 새 링크 추가
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/assets/css/page/mega-menu-glow.css';
        link.setAttribute('data-mega-menu-style', 'glow');
        document.head.appendChild(link);
      }
      // 이미 올바른 링크가 있으면 아무것도 하지 않음
    } else {
      // 기본 스타일: 기존 링크가 있으면 제거
      if (existingMegaMenuStyleLink) {
        existingMegaMenuStyleLink.remove();
      }
    }

  }, [themeConfig]);

  // window.__themeConfig 동기화 (themeConfig 변경 시마다 업데이트)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__themeConfig = deepClone(themeConfig);
    }
  }, [themeConfig]);

  // ThemeContext 인스턴스를 전역으로 노출 (ThemeConfig와의 동기화를 위해)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__ThemeContextInstance = {
        themeConfig,
        get,
        set,
        setConfig: setThemeConfig,
        reset,
        toJSON,
        fromJSON,
        getStorageMode,
        setStorageMode,
        showThemePanel,
        toggleThemePanel
      };
    }
  }, [themeConfig, get, set, reset, toJSON, fromJSON, getStorageMode, setStorageMode, showThemePanel, toggleThemePanel]);

  return (
    <ThemeContext.Provider value={{
      themeConfig,
      get,
      set,
      setConfig: setThemeConfig,
      reset,
      toJSON,
      fromJSON,
      getStorageMode,
      setStorageMode,
      showThemePanel,
      toggleThemePanel
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
